import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PATH_ADMIN_USER_EDIT } from 'routing/path'
import PageHeader from 'components/page-header'
import ValidatedField from 'components/controls/validated-field';
import ValidatedSelectField from 'components/controls/validated-select-field';
import { useGetUserQuery, useAddUserMutation, useSaveUserMutation } from 'actions/admin-user-api';
import { translationApi  } from 'actions/translation-api';
import getUserStatusDescription, { USER_STATUS_INACTIVE, USER_STATUS_ACTIVE } from 'data/enums/user-status-enum';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";

const UserEdit = () => {
	const navigate = useNavigate();
	const params = useParams();
	const userLogin = params.login;

	const { data: user, isLoading: loadUserInProgress, isFetching: fetchUserInProgress } = useGetUserQuery(userLogin, userLogin ? { refetchOnMountOrArgChange: true } : { skip: true } );
	const [ addUser, { isLoading: addUserInProgress }] = useAddUserMutation();
	const [ saveUser, { isLoading: saveUserInProgress }] = useSaveUserMutation();
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	if (userLogin && !loadUserInProgress && !fetchUserInProgress && !user) {
		// user load finished, but user not found
		navigate("/404");
		return;
	}

	const getPageHeader = () => {
		if (!userLogin) {
			// new user
			return <PageHeader title={translations.newUser} breadcrumbs={[]} breadcrumbsCurrentPage={translations.newUser} />
		}
	
		// existing user
		if (loadUserInProgress || fetchUserInProgress) {
			return <PageHeader title="..." breadcrumbs={[]} breadcrumbsCurrentPage="..." />
		}

		return <PageHeader title={`${user.firstName} ${user.lastName}`} breadcrumbs={[]} breadcrumbsCurrentPage={`${translations.user} ${user.firstName} ${user.lastName}`} />
	}

	const initializeUser = userLogin && !loadUserInProgress && !fetchUserInProgress;
	const initialValues = {
		firstName: initializeUser ? user.firstName : "",
		lastName: initializeUser ? user.lastName : "",
		login: initializeUser ? user.login : "",
		status: initializeUser ? user.status : USER_STATUS_ACTIVE,
		email: initializeUser ? user.email : "",
		phone: initializeUser ? user.phone : ""
	};

	const userStatusesToDropDownList = [
		[USER_STATUS_ACTIVE, getUserStatusDescription(USER_STATUS_ACTIVE, translations)],
		[USER_STATUS_INACTIVE, getUserStatusDescription(USER_STATUS_INACTIVE, translations)]
	];
	
	const validationSchema = Yup.object().shape({
		firstName: Yup.string()
			.required(translations.warningFieldRequired),
		lastName: Yup.string()
		.required(translations.warningFieldRequired),
		login: Yup.string()
		.required(translations.warningFieldRequired),
		email: Yup.string()
			.email(translations.warningValueInvalid),
		phone: Yup.string()
			.max(30, translations.warningValueTooLong),
	});
	
	const submitDisabled = addUserInProgress || saveUserInProgress;
	const handleSubmit = async (fields) => {
		const userToUpsert = {
			userId: user?.userId,
			firstName: fields.firstName,
			lastName: fields.lastName,
			login: fields.login,
			status: parseInt(fields.status),
			email: fields.email,
			phone: fields.phone
		};

		if (userToUpsert.userId) {
			const result = await saveUser(userToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.changesSaved);
			}
		}
		else {
			const result = await addUser(userToUpsert);
			if ('error' in result) {
				showErrorNotification(result.error);
			}
			else {
				showSuccessNotification(translations.userAdded);
				navigate(PATH_ADMIN_USER_EDIT + userToUpsert.login);
				return;
			}
		}
	}

	const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return (
		<div>
			{ getPageHeader() }
			{ <Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={ async (fields) => { await handleSubmit(fields) }}>
				{props => (
					<Form autoComplete={randomAutoComplete}>
						<div className="col-12 mb-4">
							<div className="card border-light shadow-sm components-section">
								<div className="card-body">
									<h2 className="h5 mb-4">{translations.basicData}</h2>
									<div className="row mb-4">
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="firstName" label={translations.firstName} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="lastName" label={translations.lastName} {...props} />
										</div>
									</div>
									<div className="row mb-4">
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="login" label={translations.login} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedSelectField name="status" label={translations.status} keyValueMap={userStatusesToDropDownList} {...props} />
										</div>
									</div>
									<div className="row mb-4">
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="email" label={translations.email} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="phone" label={translations.phone} maxLength="30" {...props} />
										</div>
									</div>
									<div className="row mb-5 mb-lg-5">
										<div className="col-lg-3 col-md-6">
											<div className="form-group mb-3">
												<button id="saveButton" type="submit" className="btn btn-primary me-2" disabled={submitDisabled ? "disabled" : null} >{translations.actionSave}</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Form>)}
			</Formik>}
		</div>
	)
} 

export default UserEdit;
